import React, { FC, FormEvent, useEffect, useState } from "react"
import { Script } from "gatsby"

import { isClientSide } from "@utils"

import AnchorLink from "@components/AnchorLink/AnchorLink"
import Button, { BUTTON_SIZES } from "@components/Button/Button"
import Modal from "@components/Modal/Modal"

import "./DemoSchedulerForm.scss"

enum NUM_EMPLOYEES {
  "1-10" = "1-10",
  "11-50" = "11-50",
  "51-100" = "51-100",
  "101-500" = "101-500",
  "501-1000" = "501-1000",
  "1000+" = "1000+",
}

const SCHEDULER_IDS = {
  DEFAULT: "hsrevops/website-ae-demo-booking",
  LARGE_COMPANY: "dan-svizeny/website-srae-demo-booking",
  SPECIAL_INDUSTRY: "zainab-allawala/partner-demo-booking",
}

const SCHEDULER_IDS_TRIALS = {
  DEFAULT: "hsrevops/trial-ae-demo-booking",
  LARGE_COMPANY: "dan-svizeny/trial-srae-demo-booking",
  SPECIAL_INDUSTRY: "zainab-allawala/partner-trial-demo-booking",
}

const CAMPAIGN_UTM_CODE = `&utm_source=email&utm_medium=email`

const getSchedulerId = (numEmployees: NUM_EMPLOYEES, isSpecialIndustry: boolean): string => {
  const IDS_TO_USE = window.location.pathname.includes("trial") ? SCHEDULER_IDS_TRIALS : SCHEDULER_IDS
  if (isSpecialIndustry) return IDS_TO_USE.SPECIAL_INDUSTRY
  if ([NUM_EMPLOYEES["501-1000"], NUM_EMPLOYEES["1000+"]].includes(numEmployees)) return IDS_TO_USE.LARGE_COMPANY
  return IDS_TO_USE.DEFAULT
}

const DemoSchedulerForm: FC = () => {
  const [currentNumEmployees, setCurrentNumEmployees] = useState<NUM_EMPLOYEES | string>("")
  const [isSpecialIndustry, setIsSpecialIndustry] = useState<boolean>(false)
  const [currentSchedulerId, setCurrentSchedulerId] = useState<string>("")
  const [campignUtmCode, setCampaignUtmCode] = useState<string>("")

  const isSmallTeam = currentNumEmployees === "1-10"

  useEffect(() => {
    if (!isClientSide()) return

    if (window.location.pathname === "/lp/switch-from-zendesk/") {
      setCampaignUtmCode(CAMPAIGN_UTM_CODE)
    }
  }, [])


  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    setCurrentSchedulerId(
      getSchedulerId(
        currentNumEmployees as NUM_EMPLOYEES,
        isSpecialIndustry
      )
    )
  }

  return (
    <>
      <form
        className="DemoSchedulerForm"
        onSubmit={handleSubmit}
      >
        <div className="select-wrapper">
          <select
            onChange={e => setCurrentNumEmployees(e.target.value)}
            required
            value={currentNumEmployees}
          >
            <option value="" disabled>What's your team size?</option>
            {Object.keys(NUM_EMPLOYEES).filter((k) => k !== NUM_EMPLOYEES["1-10"]).map(numEmployees => (
              <option key={numEmployees} value={numEmployees}>{numEmployees}</option>
            ))}
            <hr />
            <option value={NUM_EMPLOYEES["1-10"]}>I have a smaller team (1-10)</option>
          </select>
        </div>
        <label>
          <input
            checked={isSpecialIndustry}
            className="size--S"
            disabled={isSmallTeam}
            onChange={() => setIsSpecialIndustry(v => !v)}
            type="checkbox"
          />
          <span
            className={`type--text-S color--text-${isSmallTeam ? "disabled" : "light"} margin-bottom--0`}>
            I work for a Government, Healthcare, or Education organization
          </span>
        </label>
        {isSmallTeam ? (
          <Button
            href="{{signupPlusUrl}}"
            size={BUTTON_SIZES.L}
          >
            Start Free Trial
          </Button>
        ) : (
          <button
            className="button button--L"
            disabled={!currentNumEmployees}
            type="submit"
          >
            Schedule a Demo
          </button>
        )}
        <p
          className="small-team-copy type--text-S color--text-light"
          style={{ opacity: isSmallTeam ? 1 : 0 }}
        >
          Jump right in with a free trial (no CC needed) or
          {" "}
          <AnchorLink href="/classes/">join a live walkthrough.</AnchorLink>
        </p>
      </form >
      {currentSchedulerId && (
        <Modal
          onClose={() => setCurrentSchedulerId("")}
          useNoPaddingBottom
          width={"min(90vw, 958px)"}
        >
          <>
            <div
              className="meetings-iframe-container"
              data-src={`https://meetings.hubspot.com/${currentSchedulerId}?embed=true${campignUtmCode}`}
            />
            <Script
              type="text/javascript"
              // NOTE: Adding a cache buster to allow the script to be loaded each time the modal is opened
              //       This is a little bit hacky, but I couldn't find a way to re-initialize with HubSpot's API :/
              src={`https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js?abcd=${Math.floor(Math.random() * 1000)}`}
            />
          </>
        </Modal>
      )
      }
    </>
  )
}

export default DemoSchedulerForm
